// SPDX-License-Identifier: AGPL-3.0-or-later

import Channels from '@app/features/channel/state/Channels';
import type {GatewaySocket} from '@app/features/gateway/transport/GatewaySocket';
import {fitsLazyRequestPayload} from '@app/features/gateway/utils/GatewayPayloadBudget';
import Navigation from '@app/features/navigation/state/Navigation';
import TypingChannelFocus from '@app/features/typing/state/TypingChannelFocus';
import VoiceChannelInterest from '@app/features/voice/state/VoiceChannelInterest';
import {FAVORITES_GUILD_ID, ME} from '@fluxer/constants/src/AppConstants';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {compareStructural, type IReactionDisposer, reaction} from 'mobx';

interface ChannelFocusConnection {
	readonly isReady: boolean;
	readonly sessionId: string | null;
	readonly socket: GatewaySocket | null;
}

interface ChannelFocusTarget {
	sessionId: string | null;
	guildId: string | null;
	typingChannelIds: Array<string> | null;
	voiceEnabled: boolean;
	voiceChannelIds: Array<string> | null;
}

interface ChannelFocusFields {
	typing_channels?: Array<string>;
	voice_channels?: Array<string> | null;
}

const CHANNEL_FOCUS_SETTLE_MS = 300;
// TEMPORARY: LAZY_REQUEST frames run on separate gateway workers, at most four at once with the rest dropped, so a
// focus frame can be lost or applied before an older one; the last frames are repeated once. Remove once the gateway
// applies a session's LAZY_REQUESTs in order without dropping them (perf31 P6 F2).
const CHANNEL_FOCUS_RESEND_MS = 3_000;
const MAX_VOICE_CHANNELS = 50;
// Session start empties every other large guild and closing the flag asks each guild for a full resync with null;
// the jitter (as in the gateway contract) keeps a large guild from receiving every client's frame at once.
const VOICE_INTEREST_JITTER_MS = 120_000;
const VOICE_INTEREST_FRAME_SPACING_MS = 250;
const NO_CHANNELS: Array<string> = [];

function resolveGuildId(channelGuildId: string | undefined): string | null {
	if (channelGuildId != null) return channelGuildId;
	const guildId = Navigation.guildId;
	return guildId == null || guildId === ME || guildId === FAVORITES_GUILD_ID ? null : guildId;
}

function readVoiceChannelIds(guildId: string, openChannelId: string | null): Array<string> {
	const channelIds = new Set<string>();
	const ownChannelId = VoiceChannelInterest.engine?.connectedChannelId ?? null;
	if (ownChannelId != null && Channels.getChannel(ownChannelId)?.guildId === guildId) channelIds.add(ownChannelId);
	if (openChannelId != null) channelIds.add(openChannelId);
	for (const channelId of VoiceChannelInterest.getRetainedChannels(guildId)) channelIds.add(channelId);
	for (const channelId of VoiceChannelInterest.getListChannels(guildId)) channelIds.add(channelId);
	return [...channelIds].slice(0, MAX_VOICE_CHANNELS);
}

function readTarget(connection: ChannelFocusConnection): ChannelFocusTarget | null {
	if (!connection.isReady) return null;
	const channelId = Navigation.channelId;
	const channel = channelId != null ? Channels.getChannel(channelId) : undefined;
	const guildId = resolveGuildId(channel?.guildId);
	const inGuild = guildId != null && channel?.guildId === guildId;
	let typingChannelIds: Array<string> | null = null;
	if (TypingChannelFocus.enabled) {
		const threadId = Navigation.threadId;
		typingChannelIds = inGuild && channelId != null ? [channelId, ...(threadId != null ? [threadId] : [])] : [];
	}
	const voiceEnabled = VoiceChannelInterest.enabled;
	let voiceChannelIds: Array<string> | null = null;
	if (voiceEnabled && guildId != null && VoiceChannelInterest.engine?.isLargeGuild(guildId) === true) {
		const openVoiceChannelId = inGuild && channel?.type === ChannelTypes.GUILD_VOICE ? channelId : null;
		voiceChannelIds = readVoiceChannelIds(guildId, openVoiceChannelId);
	}
	return {sessionId: connection.sessionId, guildId, typingChannelIds, voiceEnabled, voiceChannelIds};
}

function buildFrames(subscriptions: Map<string, ChannelFocusFields>): Array<Record<string, ChannelFocusFields>> {
	const frames: Array<Record<string, ChannelFocusFields>> = [];
	for (const [guildId, fields] of subscriptions) {
		const frame = {[guildId]: fields};
		if (fitsLazyRequestPayload({subscriptions: frame})) {
			frames.push(frame);
			continue;
		}
		for (const [key, value] of Object.entries(fields)) frames.push({[guildId]: {[key]: value}});
	}
	return frames;
}

export function installGatewayChannelFocusReaction(connection: ChannelFocusConnection): IReactionDisposer {
	let settleTimer: ReturnType<typeof setTimeout> | null = null;
	let resendTimer: ReturnType<typeof setTimeout> | null = null;
	let emptyTimer: ReturnType<typeof setTimeout> | null = null;
	let lastSent: {sessionId: string | null; guildId: string | null; typing: boolean} | null = null;
	let voiceSessionId: string | null = null;
	const voiceSent = new Map<string, ReadonlySet<string>>();
	const voiceResyncPending = new Set<string>();
	let voiceResyncNotBefore = 0;
	let voiceWasEnabled = false;

	function clearTimers(): void {
		if (settleTimer != null) {
			clearTimeout(settleTimer);
			settleTimer = null;
		}
		if (resendTimer != null) {
			clearTimeout(resendTimer);
			resendTimer = null;
		}
	}

	function cancelEmptying(): void {
		if (emptyTimer != null) {
			clearTimeout(emptyTimer);
			emptyTimer = null;
		}
	}

	function emptyNextLargeGuild(): void {
		emptyTimer = null;
		const target = readTarget(connection);
		if (target == null || !target.voiceEnabled) return;
		const guildId = VoiceChannelInterest.engine
			?.largeGuildIds()
			.find((id) => id !== target.guildId && !voiceSent.has(id));
		if (guildId == null) return;
		const subscriptions = new Map<string, ChannelFocusFields>();
		putVoiceList(subscriptions, guildId, NO_CHANNELS);
		for (const frame of buildFrames(subscriptions)) connection.socket?.updateGuildSubscriptions({subscriptions: frame});
		emptyTimer = setTimeout(emptyNextLargeGuild, VOICE_INTEREST_FRAME_SPACING_MS);
	}

	function scheduleEmptying(): void {
		cancelEmptying();
		emptyTimer = setTimeout(emptyNextLargeGuild, Math.floor(Math.random() * VOICE_INTEREST_JITTER_MS));
	}

	function putFields(
		subscriptions: Map<string, ChannelFocusFields>,
		guildId: string,
		fields: ChannelFocusFields,
	): void {
		subscriptions.set(guildId, {...subscriptions.get(guildId), ...fields});
	}

	function putVoiceList(
		subscriptions: Map<string, ChannelFocusFields>,
		guildId: string,
		channelIds: Array<string>,
	): void {
		const next = new Set(channelIds);
		VoiceChannelInterest.setAcceptedChannels(guildId, next);
		voiceSent.set(guildId, next);
		voiceResyncPending.delete(guildId);
		putFields(subscriptions, guildId, {voice_channels: channelIds});
	}

	function flush(): void {
		settleTimer = null;
		const target = readTarget(connection);
		if (target == null) return;
		if (voiceSessionId !== target.sessionId) {
			voiceSent.clear();
			voiceResyncPending.clear();
			VoiceChannelInterest.resetAccepted();
			voiceSessionId = target.sessionId;
			if (target.voiceEnabled) scheduleEmptying();
		}
		const subscriptions = new Map<string, ChannelFocusFields>();
		const leftGuildId =
			lastSent?.sessionId === target.sessionId && lastSent.guildId !== target.guildId ? lastSent.guildId : null;
		if (leftGuildId != null) {
			if (target.typingChannelIds != null && lastSent?.typing) {
				putFields(subscriptions, leftGuildId, {typing_channels: []});
			}
			if (target.voiceEnabled && voiceSent.has(leftGuildId)) {
				putVoiceList(subscriptions, leftGuildId, []);
			}
		}
		if (target.guildId != null) {
			if (target.typingChannelIds != null) {
				putFields(subscriptions, target.guildId, {typing_channels: target.typingChannelIds});
			}
			if (target.voiceChannelIds != null) {
				putVoiceList(subscriptions, target.guildId, target.voiceChannelIds);
			}
		}
		if (!target.voiceEnabled && target.guildId != null && voiceResyncPending.has(target.guildId)) {
			const waitMs = voiceResyncNotBefore - Date.now();
			if (waitMs > 0) {
				settleTimer = setTimeout(flush, waitMs);
			} else {
				voiceResyncPending.delete(target.guildId);
				VoiceChannelInterest.setAcceptedChannels(target.guildId, null);
				putFields(subscriptions, target.guildId, {voice_channels: null});
			}
		}
		lastSent = {sessionId: target.sessionId, guildId: target.guildId, typing: target.typingChannelIds != null};
		if (subscriptions.size === 0) return;
		const frames = buildFrames(subscriptions);
		for (const frame of frames) connection.socket?.updateGuildSubscriptions({subscriptions: frame});
		resendTimer = setTimeout(() => {
			resendTimer = null;
			if (!compareStructural(readTarget(connection), target)) return;
			for (const frame of frames) connection.socket?.updateGuildSubscriptions({subscriptions: frame});
		}, CHANNEL_FOCUS_RESEND_MS);
	}

	const disposeReaction = reaction(
		() => readTarget(connection),
		(target) => {
			clearTimers();
			if (target == null) return;
			const voiceEnabled = target.voiceEnabled;
			if (voiceWasEnabled && !voiceEnabled) {
				cancelEmptying();
				for (const guildId of voiceSent.keys()) voiceResyncPending.add(guildId);
				voiceSent.clear();
				VoiceChannelInterest.resetAccepted();
				voiceResyncNotBefore = Date.now() + Math.floor(Math.random() * VOICE_INTEREST_JITTER_MS);
			} else if (!voiceWasEnabled && voiceEnabled && voiceSessionId === target.sessionId) {
				scheduleEmptying();
			}
			voiceWasEnabled = voiceEnabled;
			settleTimer = setTimeout(flush, CHANNEL_FOCUS_SETTLE_MS);
		},
		{equals: compareStructural, fireImmediately: true},
	);
	const disposeOwnChannelReaction = reaction(
		() => VoiceChannelInterest.engine?.connectedChannelId ?? null,
		(_channelId, previousChannelId) => {
			const previousGuildId = previousChannelId != null ? Channels.getChannel(previousChannelId)?.guildId : undefined;
			if (previousGuildId != null) VoiceChannelInterest.purge(previousGuildId);
		},
	);
	return Object.assign(() => {
		disposeReaction();
		disposeOwnChannelReaction();
		clearTimers();
		cancelEmptying();
	}, disposeReaction);
}
